import Box from "@mui/material/Box"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { type FC, useState } from "react"

import { useCostBreakdownTable } from "../hooks/useCostBreakdownTable"
import { useUpdateGrCostBreakdownCostSource } from "../hooks/useUpdateGrCostBreakdownCostSource"

import { CostBreakdownDialogs } from "./CostBreakdownDialogs"
import { CostBreakdownFilterDrawer, CostBreakdownTable } from "./CostBreakdownTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { GoodsReceiptFilterChips } from "@/screens/WarehouseReceiving/components"

interface EditingCostBreakdown {
  goodsReceipt: GoodsReceipt
  purchaseOrderLineId?: string
}

export const CostBreakdownContainer: FC = () => {
  const navigate = useNavigate({ from: "/app/procurement/cost-breakdown" })
  const searchParams = useSearch({
    from: "/app/procurement/_layout/cost-breakdown",
    strict: true,
  })

  const {
    searchQuery,
    warehouseAreaIds,
    warehouseOptions,
    isWarehousesFetching,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleRefetch,
  } = useCostBreakdownTable()

  const { updateCostSource } = useUpdateGrCostBreakdownCostSource()

  const isProductCostCompleted = searchParams.isProductCostCompleted ?? []

  const isManagedInAs400 = searchParams.isManagedInAs400 ?? []

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingCostBreakdown, setEditingCostBreakdown] = useState<EditingCostBreakdown | undefined>(undefined)

  const handleEdit = (goodsReceipt: GoodsReceipt, purchaseOrderLineId?: string) => {
    setEditingCostBreakdown({ goodsReceipt, purchaseOrderLineId })
    setIsFormOpen(true)
  }

  const handleFormClose = () => {
    setIsFormOpen(false)
    setEditingCostBreakdown(undefined)
  }

  const handleFormSubmit = () => {
    handleRefetch()
    handleFormClose()
  }

  const handleCostSourceChange = async (goodsReceipt: GoodsReceipt) => {
    const isSuccess = await updateCostSource(goodsReceipt.goodsReceiptId, !goodsReceipt.isCostManagedInAs400)

    if (isSuccess) handleRefetch()
  }

  const handleFilterToggle = () => setIsFilterOpen((prev) => !prev)

  const handleCompletedChange = (isProductCostCompleted?: boolean[]) => {
    ;(navigate as any)({
      search: (prev: Record<string, any>) => ({
        ...prev,
        isProductCostCompleted,
      }),
      replace: true,
    })
  }

  const handleManagedInAs400Change = (isManagedInAs400?: boolean[]) => {
    ;(navigate as any)({
      search: (prev: Record<string, any>) => ({
        ...prev,
        isManagedInAs400,
      }),
      replace: true,
    })
  }

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by GR, Supplier, Tracking, Container or PO'
          defaultValue={searchQuery}
          onChange={(event) => handleSearchChange(event.target.value)}
          className='w-full max-w-[524px]'
        />
        <Button appearance='outlined' startIcon={<FilterAltOutlined />} onClick={handleFilterToggle}>
          Filter
        </Button>
      </Box>

      {warehouseAreaIds.length > 0 && (
        <GoodsReceiptFilterChips
          warehouseAreaIds={warehouseAreaIds}
          warehouseOptions={warehouseOptions}
          onRemove={handleFilterRemove}
        />
      )}

      <CostBreakdownTable onEdit={handleEdit} onCostSourceChange={handleCostSourceChange} />

      <CostBreakdownFilterDrawer
        isOpen={isFilterOpen}
        warehouseAreaIds={warehouseAreaIds}
        warehouseOptions={warehouseOptions}
        isWarehousesFetching={isWarehousesFetching}
        isProductCostCompleted={isProductCostCompleted}
        isManagedInAs400={isManagedInAs400}
        onToggle={handleFilterToggle}
        onChange={handleFilterChange}
        onCompletedChange={handleCompletedChange}
        onManagedInAs400Change={handleManagedInAs400Change}
        onClear={handleFilterClear}
      />

      {isFormOpen && editingCostBreakdown && (
        <CostBreakdownDialogs
          goodsReceipt={editingCostBreakdown.goodsReceipt}
          purchaseOrderLineId={editingCostBreakdown.purchaseOrderLineId}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </Box>
  )
}
